import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@apollo/client/react'
import showDate, { showTime } from 'helpers/date/showDate'

import type { VersionRevision } from 'js/components/VersionHistory/VersionListItem/VersionListItem.types'
import useGetVersionUrlParameter from 'js/files/helpers/useGetVersionUrlParameter'
import type { PageEditorRowType } from 'js/page/PageEditor/PageEditor.types'

import {
    GET_PAGE_EDITOR_REVISION_CONTENT,
    GET_PAGE_EDITOR_REVISIONS,
} from './components/PageEditorRevisions/PageEditorRevisions.queries'

export const PAGE_EDITOR_REVISIONS_PAGE_SIZE = 10

export type PageEditorRevision = VersionRevision & {
    content?: {
        rows?: PageEditorRowType[]
    } | null
}

const getRevisionTitle = (
    revision: Pick<PageEditorRevision, 'description' | 'timeCreated'>,
) => {
    const date = `${showDate(revision.timeCreated, false, false)}, ${showTime(
        revision.timeCreated,
    )}`

    return revision.description ? `${revision.description} (${date})` : date
}

const usePageEditorRevisions = (guid: string) => {
    // Revision id from the URL.
    const versionParam = useGetVersionUrlParameter()
    // Open the popover if URL has a revision id.
    const [revisionsPopoverOpen, setRevisionsPopoverOpen] = useState(
        versionParam !== null,
    )
    // True while paging the list to find the URL revision.
    const [isFetchingTargetRevision, setIsFetchingTargetRevision] =
        useState(false)
    // True when paging stopped without finding the guid (empty page or end of list).
    const [revisionLookupExhausted, setRevisionLookupExhausted] =
        useState(false)
    const fetchMoreInFlightRef = useRef(false)

    const {
        loading: revisionsLoading,
        data,
        fetchMore,
    } = useQuery<any>(GET_PAGE_EDITOR_REVISIONS, {
        variables: {
            guid,
            offset: 0,
            limit: PAGE_EDITOR_REVISIONS_PAGE_SIZE,
        },
        fetchPolicy: 'cache-first',
    })

    const revisions = data?.revisions
    const revisionTotal = revisions?.total ?? 0

    const revisionEdges = useMemo(
        () => (revisions?.edges?.filter(Boolean) as PageEditorRevision[]) || [],
        [revisions?.edges],
    )

    const foundInList = useMemo(() => {
        if (!versionParam) {
            return false
        }

        return revisionEdges.some((revision) => revision.guid === versionParam)
    }, [versionParam, revisionEdges])

    // Reset lookup state when the URL revision changes.
    useEffect(() => {
        setRevisionLookupExhausted(false)
        setIsFetchingTargetRevision(false)
        fetchMoreInFlightRef.current = false
    }, [versionParam])

    // URL revision is missing after all list pages are loaded.
    const revisionNotFound = Boolean(
        versionParam &&
        !revisionsLoading &&
        revisions &&
        !foundInList &&
        (revisionLookupExhausted || revisionEdges.length >= revisionTotal),
    )

    // Keep fetching list pages until the URL guid is in the window (or exhausted).
    useEffect(() => {
        if (!versionParam || revisionsLoading || !revisions) {
            return
        }

        if (
            foundInList ||
            revisionLookupExhausted ||
            revisionEdges.length >= revisionTotal
        ) {
            setIsFetchingTargetRevision(false)
            return
        }

        if (fetchMoreInFlightRef.current) {
            return
        }

        let cancelled = false
        const offsetBeforeFetch = revisionEdges.length
        fetchMoreInFlightRef.current = true
        setIsFetchingTargetRevision(true)

        fetchMore({
            variables: {
                offset: offsetBeforeFetch,
                limit: PAGE_EDITOR_REVISIONS_PAGE_SIZE,
            },
        })
            .then((result) => {
                if (cancelled) {
                    return
                }

                const edgesAfter =
                    result.data?.revisions?.edges?.filter(Boolean) ?? []

                // Apollo may return either the new page or the merged list.
                const fetchedCount =
                    edgesAfter.length <= offsetBeforeFetch
                        ? edgesAfter.length
                        : edgesAfter.length - offsetBeforeFetch

                // Stop if the server returned nothing new (avoids an endless loop
                // when total is higher than the edges that actually exist).
                if (fetchedCount === 0) {
                    setRevisionLookupExhausted(true)
                    setIsFetchingTargetRevision(false)
                }
            })
            .finally(() => {
                fetchMoreInFlightRef.current = false
            })

        return () => {
            cancelled = true
        }
    }, [
        versionParam,
        revisionsLoading,
        revisions,
        foundInList,
        revisionLookupExhausted,
        revisionEdges.length,
        revisionTotal,
        fetchMore,
    ])

    // Old revisions are addressed by list offset because there is no query by guid.
    const activeRevisionIndex = useMemo(() => {
        if (!versionParam) {
            return -1
        }

        const index = revisionEdges.findIndex(
            (revision) => revision.guid === versionParam,
        )

        // First item is latest, so not an old revision.
        if (index <= 0) {
            return -1
        }

        return index
    }, [versionParam, revisionEdges])

    // Load row content separately so the revisions list stays lightweight.
    const { data: revisionContentData, loading: revisionContentLoading } =
        useQuery<any>(GET_PAGE_EDITOR_REVISION_CONTENT, {
            variables: {
                guid,
                offset: activeRevisionIndex,
                limit: 1,
            },
            skip: activeRevisionIndex < 0,
            fetchPolicy: 'cache-first',
        })

    const activeRevision = useMemo(() => {
        if (activeRevisionIndex < 0) {
            return null
        }

        const revision = revisionEdges[activeRevisionIndex]
        if (!revision) {
            return null
        }

        const contentRevision =
            revisionContentData?.revisions?.edges?.[0] ?? null

        // Ignore stale content while the next revision is loading.
        if (contentRevision?.guid !== revision.guid) {
            return revision
        }

        return {
            ...revision,
            content: contentRevision.content,
        }
    }, [activeRevisionIndex, revisionEdges, revisionContentData])

    // Subtitle when viewing an old revision.
    const revisionTitle = activeRevision
        ? getRevisionTitle(activeRevision)
        : null

    // Avoid blank editor while the URL revision (or its content) is still loading.
    const isResolvingRevision = Boolean(
        versionParam &&
        !revisionNotFound &&
        (revisionsLoading ||
            isFetchingTargetRevision ||
            !foundInList ||
            (activeRevisionIndex >= 0 &&
                (!activeRevision?.content?.rows || revisionContentLoading))),
    )

    return {
        versionParam,
        revisionsLoading,
        revisionEdges,
        revisionTotal,
        fetchMore,
        activeRevision,
        revisionTitle,
        revisionsPopoverOpen,
        setRevisionsPopoverOpen,
        isResolvingRevision,
        revisionNotFound,
    }
}

export default usePageEditorRevisions
